import React, { createContext, useContext, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import TreeNodeTitle from '../components/tree-component/tree-node-title.jsx';

import { apiGetMenuTypeDefinition } from '../routes/menu-type-definition/api.js';
import auth from '../app-auth.js';
import CMXService from '../services/cmx';

import { GOLD_COMPANY_ID } from '../constants.js';

import { 
    currentPageState, 
    menuTypesPage, 
    definitionPage, 
    configurationPage 
} from './page-state.js';
import { getStringWithFallback } from '../utils/strings.js';

export const NavigationContext = createContext();

export const NavigationContextProvider = ( props ) => {
    const { children, isUmbrella } = props;
    const [ navigationState, setNavigationState ] = useState( { activeMenuTypeData: {} } );
    const [ deletedItems, setDeletedItems ] = useState( [] );
    const [ deletedMenus, setDeletedMenus ] = useState( [] );
    const [ editItem, setEditItem ] = useState( {} );
    const [ editSubmenu, setEditSubmenu ] = useState( {} );
    const [ treeData, setTreeData ] = useState( {} );
    const [ changesMade, setChangesMade ] = useState( false );
    const [ loading, setLoading ] = useState( false );
    const [ currentPage, setCurrentPage ] = useState( {} );
    const [ expandedTreeKeys, setExpandedTreeKeys ] = useState( [] );

    const removeKeyRecursively = (  data, targetKey ) => {
        // 1. Handle Arrays: Process every element in the list
        if ( Array.isArray( data ) ) {
            return data.map( item => removeKeyRecursively( item, targetKey ) );
        }
        // 2. Handle Objects: Filter out the target key and recurse into values
        if ( typeof data === 'object' && data !== null ) {
            return Object.keys( data ).reduce( ( acc, key ) => {
            if ( key !== targetKey ) {
                acc[ key ] = removeKeyRecursively( data[ key ], targetKey );
            }
            return acc;
            }, {} );
        }
        // 3. Handle Primitives: Return strings, numbers, booleans, etc. as-is
        return data;
    };

    const setActiveMenuTypeData = ( newData ) => {
        reOrderMenuTypeData();
        sortMenuTypeData( newData.items );
        const newDataWithoutLabel = removeKeyRecursively( newData, 'label' );
        setNavigationState( { ...navigationState, activeMenuTypeData: newDataWithoutLabel } );
        return newData;
    };

    const fetchMenuTypeData = async ( route, fetch ) => {
        const {
            menuType
        } = route.params;
        const typesMatch = navigationState.activeMenuTypeData.type && navigationState.activeMenuTypeData.type.toLowerCase() === menuType.toLowerCase();
        let data;
        if ( navigationState.activeMenuTypeData.id && typesMatch ) {
            data = navigationState.activeMenuTypeData;
        } else {
            data = await apiGetMenuTypeDefinition( menuType, fetch );
        }
        setActiveMenuTypeData( data );
        // sortMenuTypeData( data.items );
        return data;
    };

    const setupTreeData = ( data, route ) => {
        const tree = createTreeData( data, route );
        setTreeData( tree );
    };

    const sortMenuTypeData = ( data ) => {
        if ( data ) {
            for ( let i = 0; i < data.length; i++ ) {
                if ( data[ i ].items ) {
                    data.sort( ( a, b ) => a.sortOrder > b.sortOrder ? 1 : -1 );
                    sortMenuTypeData( data[ i ].items );
                } 
                if ( data[ i ].item ) {
                    data.sort( ( a, b ) => {
                        const aSortOrder = a.item ? a.item.sortOrder : a.sortOrder;
                        const bSortOrder = b.item ? b.item.sortOrder : b.sortOrder;
                        return aSortOrder > bSortOrder ? 1 : -1;
                    } );
                } 
                    if ( data[ i ].menu ) {
                        sortMenuTypeData( data[ i ].menu.items );
                    }
            }
        }
    };

    const reOrderMenuTypeData = ( ) => {
        if ( Object.keys( navigationState.activeMenuTypeData ).length > 0 && navigationState.activeMenuTypeData.items ) {
            navigationState.activeMenuTypeData.items.forEach( ( item, i ) => {
                item.sortOrder = i + 1;
                if ( item.items ) {
                    setSortOrdersRecursively( item.items );
                }
            } );
        }
    };

    const setSortOrdersRecursively = ( data ) => {
        data.forEach( ( item, i ) => {
            if ( item.item ) {
                item.item.sortOrder = i + 1;
                if ( item.menu ) {
                    item.menu.sortOrder = i + 1;
                    setSortOrdersRecursively( item.menu.items );
                }
            }
        } );
    };

    const createTreeData = ( menuData, route ) => {
        if ( menuData.id ) {
            return menuData.items.map( item => {
                return createTreeItems( item, undefined, menuData, route, undefined, true );
            } );
        } else if ( menuData.menu ) {
            return menuData.menu.items.map( item => {
                return createTreeItems( item, undefined, menuData, route, undefined, true );
            } );
        }
    };

    const createTreeItems = ( itemObject, previousKeyName, menuData, route, locArr, topLevel ) => {
        const returnObject = {};
        const label = itemObject.item ? itemObject.item.labelKey : itemObject.labelKey;
        let keyName = previousKeyName ? `${ previousKeyName }-${ label }` : label;
        // If an item has an id, append it to guarantee uniqueness even when labels repeat.
        if ( itemObject?.item?.id || itemObject?.id ) {
            keyName = `${ keyName }_${ itemObject?.item?.id?.replaceAll( '-', '_' ) || itemObject?.id?.replaceAll( '-', '_' ) }`; 
        }
        returnObject.key = keyName;
        returnObject.id = itemObject.menu ? itemObject.menu.id : ( itemObject.item ? itemObject.item.id : itemObject.id );
        if ( itemObject.items ) {
            const locationArr = locArr ? locArr : [];
            locationArr.push( { name: label, id: itemObject.item ? itemObject.item.id : itemObject.id } );
            returnObject.children = itemObject.items.map( item => {
                return createTreeItems( item, keyName, menuData, route, locationArr );
            } );
        } else if ( itemObject.menu ) {
            const locationArr = locArr ? locArr : [];
            locationArr.push( { name: label, id: itemObject.item ? itemObject.item.id : itemObject.id } );
            returnObject.children = itemObject.menu.items.map( item => {
                return createTreeItems( item, keyName, menuData, route, locationArr );
            } );
        }

        returnObject.className =  topLevel ? 'top-level' : '';
        returnObject.checkable = topLevel ? true : false;
        returnObject.title =
            <TreeNodeTitle
                label={ label }
                route={ route }
                id={ itemObject.menu ? itemObject.menu.id : ( itemObject.item ? itemObject.item.id : itemObject.id ) }
                menuTypeData={ menuData }
                enabled={ itemObject.items ? itemObject.enabled : itemObject.item ? itemObject.item.enabled : itemObject.enabled }
                handleChangeEnabled={ handleChangeEnabled }
                deleteMenuItem={ deleteMenuItem }
                locationArray={ locArr }
                children={ returnObject.children }
                companyId={ itemObject.item ? itemObject.item.companyId : itemObject.companyId }
                type={ itemObject.type }
                topLevel={ topLevel }
                goldId={ itemObject.menu ? itemObject.menu.goldId : ( itemObject.item ? itemObject.item.goldId : itemObject.goldId ) }
                isUmbrella={ isUmbrella }
            />;
        return returnObject;
    };

    const findAndUpdateSubmenu = ( newMenuTypeData, submenu, route ) => {
        newMenuTypeData.items.forEach( item => {
            if ( item.id === submenu.id ) {
                // item = submenu;
                submenu = item;
                setEditSubmenu( submenu );
                setupTreeData( item, route );
            } else {
                if ( item.items ) {
                    item.items.forEach( i => {
                        findAndUpdateSubmenuRecurse( item, submenu, route );
                    } );
                }
            }
        } );
    };

    const findAndUpdateSubmenuRecurse = ( item, submenu, route ) => {
        if ( item.menu ) {
            if ( item.menu.id === submenu.id ) {
                item.menu = submenu;
                setupTreeData( item, route );
            } else {
                item.menu.items.forEach( i => {
                    findAndUpdateSubmenuRecurse( i, submenu, route );
                } );
            }
        } else {
            if ( item.items ) {
                item.items.forEach( i => {
                    findAndUpdateSubmenuRecurse( i, submenu, route );
                } );
            } 
        }
        
    };

    const findNestedRootItemAndApply = ( id, route, method, companyId ) => {
        // start at the top, locate id
        for ( let i = 0; i < navigationState.activeMenuTypeData?.items?.length; i++ ) {
            const item = navigationState.activeMenuTypeData.items[ i ];
            if ( item.id === id ) {
                const returnValue = method( item, companyId, route, navigationState.activeMenuTypeData, i );
                reOrderMenuTypeData();
                return returnValue;
            } else {
                if ( item.items ) {
                    item.items.forEach( ( innerItem, j ) => {
                        const returnValue = findNestedInnerItemAndApply( id, route, method, innerItem, companyId, item, j );
                        reOrderMenuTypeData( );
                        return returnValue;
                    } );
                }
            }
        }

    };

    const findNestedInnerItemAndApply = ( id, route, method, item, companyId, arr, position ) => {
        if ( item.item.id === id ) {
            return method( item, companyId, route, arr, position );
        } else if ( item.menu ) {
            if ( item.menu.id === id ) {
                return method( item, companyId, route, arr, position );
            } else {
                item.menu.items.forEach( ( innerItem, i ) => {
                    if ( innerItem.item.id === id ) {
                        return method( innerItem, companyId, route, item.menu, i );
                    } else if ( innerItem.menu ) {
                        if ( innerItem.menu.id === id ) {
                            return method( innerItem, companyId, route, item.menu, i );
                        } else {
                            return findNestedInnerItemAndApply( id, route, method, innerItem, companyId, item.menu, i );
                        }
                    }
                } );
            }
        }
    };

    const updateToCompanyItemRecursively = ( item, companyId, parentMenuId ) => {

        //check item, update to company item
        //if it's got a menu, do update recursively or something
        if ( item.id ) {
            if ( isCompanyConfiguration( item, companyId ) ) {
                const newId = uuidv4();
                setupCompanyItem( item, newId, companyId );
                if ( item.items ) {
                    item.items.forEach( i => {
                        updateToCompanyItemRecursively( i, companyId, newId );
                    } );
                }
            }
        } else if ( item.item ) {
            if ( isCompanyConfiguration( item.item, companyId ) ) {
                const newId = uuidv4();
                setupCompanyItem( item, newId, companyId, parentMenuId );
                if ( item.menu ) {
                    item.menu.items.forEach( i => {
                        updateToCompanyItemRecursively( i, companyId, item.menu.id );
                    } );
                }
            }
        }


        const newActiveMenuTypeData = navigationState.activeMenuTypeData;
        setActiveMenuTypeData( newActiveMenuTypeData );

        return item;
    };

    const setupCompanyItem = ( item, id, companyId, parentMenuId ) => {
        if ( item.item ) {
            item.item.goldId = item.item.id;
            item.item.id = id;
            item.item.companyId = companyId;
            item.item.label = getStringWithFallback( item.item.labelKey, companyId, isUmbrella );
            if ( parentMenuId ) {
                item.item.parentMenuId = parentMenuId;
            }
            if ( item.menu ) {
                item.menu.goldId = item.menu.id;
                const newId = uuidv4();
                item.menu.id = newId;
                item.item.subMenuId = newId;
                item.menu.companyId = companyId;
                if ( item.menu.labelKey ) {
                    item.menu.labelKey = item.menu.labelKey.split( '.' ).pop().toLowerCase();
                } else {
                    item.menu.labelKey = item.item.labelKey.toLowerCase();
                    item.menu.label = getStringWithFallback( item.menu.labelKey, companyId, isUmbrella );
                }
            }
        }

        if ( item.id ) {
            item.goldId = item.id;
            item.id = id;
            item.companyId = companyId;
            item.label = getStringWithFallback( item.labelKey, companyId, isUmbrella );
        }
    };

    const handleChangeEnabled = ( id, route ) => {
        const { companyId } = route.params;
        setChangesMade( true );
        const isSubmenu = Object.keys( editSubmenu ).length > 0 ? true : false;
        const menuData = isSubmenu ? editSubmenu : navigationState.activeMenuTypeData;
        findNestedRootItemAndApply( id, route, changeEnabled, companyId );
        if ( isSubmenu ) {
            const newMenuTypeData = navigationState.activeMenuTypeData;
            findAndUpdateSubmenu( newMenuTypeData, menuData, route );
        } else {
            setActiveMenuTypeData( JSON.parse( JSON.stringify( menuData ) ) );
            const tree = createTreeData( menuData, route );
            setTreeData( tree );
        }
    };

    const isCompanyConfiguration = ( item, companyId ) => {
        if ( item.companyId === GOLD_COMPANY_ID && companyId !== GOLD_COMPANY_ID ) {
            return true;
        }
        return false;
    };

    const recursivelyFilterNonCompanyItems = ( data, companyId ) => {
        const filteredData = data;
        filteredData.items = filteredData.items.filter( i => {
            if ( i.companyId ) {
                return i.companyId === companyId;
            } else {
                return i.item.companyId === companyId;
            }
        } );
        filteredData.items.forEach( i => {
            if ( i.items ) {
                recursivelyFilterNonCompanyItems( i, companyId );
            }
        } );
        if ( filteredData.menus ) {
            filteredData.menus = filteredData.menus.filter( m => m.companyId === companyId );
            filteredData.menus.forEach( m => recursivelyFilterNonCompanyItems( m, companyId ) );
        }
        return filteredData;
    };

    const changeEnabled = ( item, companyId ) => {
        let parentMenuId;
        if ( item.id ) {
            if ( isCompanyConfiguration( item, companyId ) ) {
                //create new company item
                const newId = uuidv4();
                setupCompanyItem( item, newId, companyId );
                parentMenuId = newId;
            }
            item.enabled = !item.enabled;
            return changeEnabledChildren( item, item.enabled, companyId, item.id );
        } else if ( item.item ) {
            if ( isCompanyConfiguration( item.item, companyId ) ) {
                // create a new company item
                const newId = uuidv4();
                setupCompanyItem( item, newId, companyId, parentMenuId );
            }
            item.item.enabled = !item.item.enabled;
            if ( item.menu ) {
                item.menu.enabled = !item.menu.enabled;
                return changeEnabledChildren( item, item.item.enabled, companyId, item.menu.id );
            }
        }
        return item;
    };

    const changeEnabledChildren = ( item, enabled, companyId, parentMenuId ) => {
        if ( item.items ) {
            item.items.forEach( innerItem => {
                if ( isCompanyConfiguration( innerItem.item, companyId ) ) {
                    // create new item
                    const newId = uuidv4();
                    setupCompanyItem( innerItem, newId, companyId, parentMenuId );
                }
                innerItem.item.enabled = enabled;
                if ( innerItem.menu ) {
                    innerItem.menu.enabled = enabled;
                    innerItem.menu.items.forEach( innerItem2 => {
                        innerItem2 = changeEnabledChildren( innerItem2, enabled, companyId, innerItem.menu.id );
                    } );
                }

            } );
        } else if ( item.item ) {
            if ( isCompanyConfiguration( item.item, companyId ) ) {
                const newId = uuidv4();
                // create new item (alter current item with company values)
                setupCompanyItem( item, newId, companyId, parentMenuId );
            }
            if ( item.menu ) {
                item.menu.items.forEach( innerItem => { 
                    innerItem = changeEnabledChildren( innerItem, enabled, companyId, item.menu.id ); 
                } );
            }
            
            item.item.enabled = enabled;
        }
        return item;
    };

    const deleteMenuItem = ( id,  route, companyId  ) => {
        findNestedRootItemAndApply( id, route, doDelete, companyId, true );
    };

    const doDelete = ( deleteItem, companyId, route, arr, position ) => {
        if ( deleteItem.items ) {
            const deletedItem = arr.items.splice( position, 1 )[ 0 ];
            const newDeletedMenus = deletedMenus;
            newDeletedMenus.push( deletedItem );
            setDeletedMenus( newDeletedMenus );
            makeTree( arr, route );
        } else if ( deleteItem.item ) {
            const deletedItem = arr.items.splice( position, 1 )[ 0 ];
            const newItems = deletedItems;
            newItems.push( deletedItem.item );
            setDeletedItems( newItems );
            makeTree( arr, route );

            if ( deletedItem.menu ) {
                const newMenus = deletedMenus;
                newMenus.push( deletedItem.menu );
                setDeletedMenus( newMenus );
            }
        } else {
            const deletedItem = arr.items.splice( position, 1 )[ 0 ];
            const newItems = deletedItems;
            newItems.push( deletedItem );
            setDeletedItems( newItems );
            makeTree( arr, route );
        }

        arr.items.forEach( ( item, i ) => {
            if ( item.item ) {
                item.item.sortOrder = i + 1;
                if ( item.menu ) {
                    item.menu.sortOrder = i + 1;
                }
            } else {
                item.sortOrder = i + 1;
            }
        } );
        const newMenuTypeData = navigationState.activeMenuTypeData;
        setActiveMenuTypeData( newMenuTypeData );
    };

    const clearState = () => {
        setNavigationState( { activeMenuTypeData: {} } );
        setDeletedItems( [] );
        setDeletedMenus( [] );
        setEditItem( {} );
        setEditSubmenu( {} );
        setTreeData( {} );
        setChangesMade( false );
        setLoading( false );
    };

    const makeTree = ( data, route ) => {
        const tree = createTreeData( data, route );
        setTreeData( tree );
        setChangesMade( true );
    };

    const setupCurrentPage = ( pageState ) => {
        let returnPage;
        switch ( pageState ) {
            case currentPageState.MENU_TYPES_PAGE:
                if ( window.location.href.indexOf( 'configuration' ) >= 0 ) {
                    setCurrentPage( configurationPage );
                    returnPage = configurationPage;
                } else {
                    setCurrentPage( menuTypesPage );
                    returnPage = menuTypesPage;
                }
                break;

            case currentPageState.DEFINITION_PAGE:
                setCurrentPage( definitionPage );
                returnPage = definitionPage;
                break;
                
            case currentPageState.CONFIGURATION_PAGE:
                setCurrentPage( configurationPage );
                returnPage = configurationPage;
                break;

            default:
                break;
        }
        return returnPage;
    };

    const reloadCmxBundle = async( fetch ) => {
        const cmxService = new CMXService( fetch );
        if ( !isUmbrella ) {
            await cmxService.loadBundles( [ 'navigation', 'studio-in-context-help' ], undefined, auth.state.claims.company_id );
        } else {
            await cmxService.loadBundWithAttributes( 'navigation' );
        }
    };

    return (
        <NavigationContext.Provider
            value={ {
                navigationState,
                editItem,
                treeData,
                changesMade,
                editSubmenu,
                loading,
                currentPage,
                menuTypesPage,
                definitionPage,
                configurationPage,
                deletedMenus,
                deletedItems,
                expandedTreeKeys,
                deleteMenuItem,
                fetchMenuTypeData,
                setEditItem,
                setActiveMenuTypeData,
                setTreeData,
                createTreeData,
                handleChangeEnabled,
                setChangesMade,
                setupTreeData,
                setEditSubmenu,
                setLoading,
                clearState,
                updateToCompanyItemRecursively,
                findNestedRootItemAndApply,
                setupCurrentPage,
                reloadCmxBundle,
                setupCompanyItem,
                setExpandedTreeKeys,
                recursivelyFilterNonCompanyItems,
            } }
        >
            { children }
        </NavigationContext.Provider>
    );
};

export const useNavigationContext = () => useContext( NavigationContext );
